const CustomSoftwareROICalculatorStyles = () => {
  return (
    <style jsx>{`
      .roi-calculator-page {
        font-family:
          -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        overflow-x: hidden;
      }

      /* Container improvements */
      .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 20px;
        width: 100%;
        box-sizing: border-box;
      }

      /* Hero Section - Fixed overlapping */
      .calculator-hero {
        background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
        color: white;
        padding: 80px 0 60px;
        position: relative;
        overflow: hidden;
      }

      .calculator-hero::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background:
          radial-gradient(
            circle at 20% 80%,
            rgba(255, 215, 0, 0.1) 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 80% 20%,
            rgba(255, 255, 255, 0.05) 0%,
            transparent 50%
          );
      }

      .hero-content {
        max-width: 800px;
        margin: 0 auto;
        text-align: center;
        position: relative;
        z-index: 1;
        padding: 0 20px;
      }

      .hero-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        padding: 8px 16px;
        border-radius: 50px;
        margin-bottom: 24px;
        border: 1px solid rgba(255, 255, 255, 0.2);
      }

      .badge-icon {
        font-size: 20px;
      }

      .badge-text {
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 1px;
      }

      .hero-title {
        font-size: clamp(2.5rem, 5vw, 3.5rem);
        font-weight: 700;
        line-height: 1.1;
        margin-bottom: 20px;
        word-wrap: break-word;
      }

      .highlight {
        background: linear-gradient(90deg, #ffd700 0%, #ffec8b 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .hero-subtitle {
        font-size: 1.25rem;
        line-height: 1.6;
        color: rgba(255, 255, 255, 0.9);
        max-width: 600px;
        margin: 0 auto 40px;
        word-wrap: break-word;
      }

      .hero-stats {
        display: flex;
        justify-content: center;
        gap: 40px;
        flex-wrap: wrap;
      }

      .stat-item {
        text-align: center;
        min-width: 120px;
      }

      .stat-value {
        font-size: 2.5rem;
        font-weight: 700;
        color: #ffd700;
        margin-bottom: 4px;
        display: block;
      }

      .stat-label {
        font-size: 0.875rem;
        color: rgba(255, 255, 255, 0.8);
        text-transform: uppercase;
        letter-spacing: 1px;
        display: block;
        white-space: nowrap;
      }

      /* Main Calculator - Fixed layout */
      .main-calculator {
        padding: 60px 20px;
        max-width: 1200px;
        margin: 0 auto;
      }

      .calculator-card {
        background: white;
        border-radius: 24px;
        box-shadow: 0 20px 60px rgba(10, 36, 88, 0.1);
        overflow: hidden;
        margin-bottom: 60px;
      }

      .calculator-header {
        padding: 40px;
        text-align: center;
        border-bottom: 1px solid #f0f2f5;
      }

      .calculator-header h2 {
        font-size: 2rem;
        color: #0a2458;
        margin-bottom: 8px;
        word-wrap: break-word;
      }

      .calculator-header p {
        color: #0a2458;
        font-size: 1.125rem;
        margin: 0;
        line-height: 1.5;
      }

      /* Input Sections - Fixed overlapping */
      .input-sections {
        padding: 0 40px 40px;
      }

      @media (max-width: 768px) {
        .input-sections {
          padding: 0 20px 30px;
        }
      }

      .input-section {
        margin-bottom: 40px;
        padding-bottom: 40px;
        border-bottom: 1px solid #f0f2f5;
      }

      .input-section:last-child {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
      }

      .section-header {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 32px;
        flex-wrap: wrap;
      }

      .section-header-content {
        flex: 1;
        min-width: 200px;
      }

      .section-icon {
        font-size: 32px;
        width: 60px;
        height: 60px;
        background: rgba(10, 36, 88, 0.1);
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .section-header h3 {
        font-size: 1.5rem;
        color: #0a2458;
        margin-bottom: 4px;
        word-wrap: break-word;
      }

      .section-header p {
        color: #0a2458;
        margin: 0;
        line-height: 1.5;
      }

      .input-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 32px;
      }

      @media (max-width: 768px) {
        .input-grid {
          grid-template-columns: 1fr;
          gap: 20px;
        }
      }

      .input-group {
        background: #f8fafc;
        border-radius: 16px;
        padding: 24px;
        transition: all 0.3s ease;
        min-height: 140px;
        display: flex;
        flex-direction: column;
      }

      .input-group:hover {
        background: #f0f7ff;
        transform: translateY(-2px);
      }

      .input-group label {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        flex-wrap: wrap;
        gap: 8px;
      }

      .label-text {
        font-weight: 600;
        color: #0a2458;
        font-size: 1.125rem;
        flex: 1;
        line-height: 1.2;
        min-width: 150px;
      }

      .label-value {
        background: white;
        padding: 6px 12px;
        border-radius: 8px;
        font-weight: 600;
        color: #1a3b8a;
        font-size: 0.875rem;
        border: 1px solid #e2e8f0;
        white-space: nowrap;
      }

      .range-slider {
        width: 100%;
        height: 8px;
        background: linear-gradient(to right, #e2e8f0, #1a3b8a);
        border-radius: 4px;
        outline: none;
        -webkit-appearance: none;
        margin: 16px 0;
      }

      .range-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 24px;
        height: 24px;
        background: #0a2458;
        border-radius: 50%;
        cursor: pointer;
        border: 4px solid white;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        transition: all 0.2s ease;
      }

      .range-slider::-webkit-slider-thumb:hover {
        transform: scale(1.1);
        background: #1a3b8a;
      }

      .slider-ticks {
        display: flex;
        justify-content: space-between;
        margin-top: 8px;
        color: #94a3b8;
        font-size: 0.75rem;
      }

      .slider-ticks span {
        display: block;
        white-space: nowrap;
      }

      /* Calculate Button Section */
      .calculate-section {
        padding: 40px;
        background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
        text-align: center;
      }

      @media (max-width: 768px) {
        .calculate-section {
          padding: 30px 20px;
        }
      }

      .calculate-btn {
        background: white;
        color: #0a2458;
        border: none;
        padding: 20px 40px;
        font-size: 1.25rem;
        font-weight: 600;
        border-radius: 16px;
        display: inline-flex;
        align-items: center;
        gap: 12px;
        cursor: pointer;
        transition: all 0.3s ease;
        margin-bottom: 16px;
        white-space: nowrap;
      }

      @media (max-width: 768px) {
        .calculate-btn {
          padding: 16px 24px;
          font-size: 1.1rem;
          width: 100%;
          justify-content: center;
        }
      }

      .calculate-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
      }

      .calculate-note {
        color: rgba(255, 255, 255, 0.8);
        font-size: 0.875rem;
        margin: 0;
      }

      /* Results Section - Fixed overlapping */
      .results-section {
        animation: slideIn 0.6s ease;
        margin-top: 40px;
      }

      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(40px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .results-header {
        text-align: center;
        margin-bottom: 60px;
        padding: 0 20px;
      }

      .results-header h2 {
        font-size: 2.5rem;
        color: #0a2458;
        margin-bottom: 12px;
        word-wrap: break-word;
      }

      .results-header p {
        color: #0a2458;
        font-size: 1.125rem;
        margin: 0;
        line-height: 1.5;
      }

      @media (max-width: 768px) {
        .results-header h2 {
          font-size: 2rem;
        }
      }

      /* Comparison Grid - Fixed layout */
      .comparison-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 40px;
        margin-bottom: 60px;
      }

      @media (max-width: 992px) {
        .comparison-grid {
          gap: 20px;
        }
      }

      @media (max-width: 768px) {
        .comparison-grid {
          grid-template-columns: 1fr;
          gap: 30px;
        }
      }

      .comparison-card {
        background: white;
        border-radius: 20px;
        overflow: hidden;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        transition: transform 0.3s ease;
        min-height: 400px;
        display: flex;
        flex-direction: column;
      }

      .comparison-card:hover {
        transform: translateY(-5px);
      }

      .comparison-card.off-shelf {
        border-top: 4px solid #94a3b8;
      }

      .comparison-card.custom {
        border-top: 4px solid #10b981;
      }

      .card-header {
        padding: 24px;
        background: #f8fafc;
        display: flex;
        align-items: center;
        gap: 12px;
        border-bottom: 1px solid #f1f5f9;
      }

      .card-icon {
        font-size: 28px;
        flex-shrink: 0;
      }

      .card-header h3 {
        font-size: 1.5rem;
        color: #0a2458;
        margin: 0;
        word-wrap: break-word;
      }

      .card-body {
        padding: 24px;
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .total-cost {
        text-align: center;
        margin-bottom: 32px;
        padding: 16px;
        background: rgba(10, 36, 88, 0.02);
        border-radius: 12px;
      }

      .cost-label {
        display: block;
        color: #0a2458;
        font-size: 0.875rem;
        margin-bottom: 8px;
      }

      .cost-value {
        font-size: 2.5rem;
        font-weight: 700;
        color: #0a2458;
        display: block;
        word-wrap: break-word;
      }

      .cost-value.saving {
        color: #10b981;
      }

      .breakdown {
        margin-bottom: 24px;
        flex: 1;
      }

      .breakdown h4 {
        color: #0a2458;
        margin-bottom: 16px;
        font-size: 1rem;
      }

      .breakdown-item {
        display: flex;
        justify-content: space-between;
        padding: 10px 0;
        border-bottom: 1px solid #f1f5f9;
        align-items: center;
        min-height: 44px;
      }

      .breakdown-item:last-child {
        border-bottom: none;
      }

      .breakdown-label {
        color: #0a2458;
        font-size: 0.9rem;
        flex: 1;
        padding-right: 10px;
        word-break: break-word;
      }

      .breakdown-value {
        font-weight: 600;
        color: #0a2458;
        font-size: 0.9rem;
        white-space: nowrap;
      }

      .card-note {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px;
        background: #fef3c7;
        border-radius: 8px;
        font-size: 0.875rem;
        color: #92400e;
        margin-top: auto;
      }

      .card-note.positive {
        background: #d1fae5;
        color: #065f46;
      }

      .note-icon {
        flex-shrink: 0;
      }

      .note-text {
        flex: 1;
        word-break: break-word;
      }

      /* Savings Highlight - Fixed text overlapping */
      .savings-highlight {
        background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
        border-radius: 20px;
        padding: 40px;
        margin-bottom: 60px;
        color: white;
        text-align: center;
        overflow: hidden;
      }

      @media (max-width: 768px) {
        .savings-highlight {
          padding: 30px 20px;
          margin-bottom: 40px;
        }
      }

      .savings-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
        flex-wrap: wrap;
      }

      .savings-icon {
        font-size: 48px;
        flex-shrink: 0;
      }

      .savings-text {
        flex: 1;
        min-width: 200px;
        text-align: left;
      }

      .savings-text h3 {
        font-size: 1.5rem;
        margin-bottom: 4px;
        word-wrap: break-word;
      }

      .savings-text p {
        font-size: 1rem;
        opacity: 0.9;
        margin: 0;
        line-height: 1.5;
      }

      .savings-amount {
        font-size: clamp(2.5rem, 5vw, 3.5rem);
        font-weight: 700;
        flex-shrink: 0;
        white-space: nowrap;
      }

      @media (max-width: 768px) {
        .savings-content {
          flex-direction: column;
          text-align: center;
          gap: 16px;
        }

        .savings-text {
          text-align: center;
        }

        .savings-amount {
          font-size: 2.5rem;
        }
      }

      /* Metrics Grid - Fixed layout */
      .metrics-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 24px;
        margin-bottom: 60px;
      }

      @media (max-width: 768px) {
        .metrics-grid {
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }
      }

      @media (max-width: 480px) {
        .metrics-grid {
          grid-template-columns: 1fr;
        }
      }

      .metric-card {
        background: white;
        border-radius: 16px;
        padding: 24px;
        text-align: center;
        box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
        transition: transform 0.3s ease;
        min-height: 150px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      .metric-card:hover {
        transform: translateY(-5px);
      }

      .metric-icon {
        font-size: 32px;
        margin-bottom: 16px;
      }

      .metric-content {
        width: 100%;
      }

      .metric-value {
        font-size: 2rem;
        font-weight: 700;
        color: #0a2458;
        margin-bottom: 4px;
        display: block;
        word-break: break-word;
      }

      .metric-label {
        color: #0a2458;
        font-size: 0.875rem;
        display: block;
        line-height: 1.4;
      }

      /* Recommendation Card - Fixed text overlapping */
      .recommendation-card {
        border-radius: 20px;
        overflow: hidden;
        margin-bottom: 60px;
        background: white;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
      }

      .recommendation-card.strong {
        border-left: 6px solid #10b981;
      }

      .recommendation-card.good {
        border-left: 6px solid #f59e0b;
      }

      .recommendation-card.moderate {
        border-left: 6px solid #8b5cf6;
      }

      .recommendation-card.consider {
        border-left: 6px solid #6b7280;
      }

      .recommendation-header {
        padding: 24px;
        background: #f8fafc;
        display: flex;
        align-items: center;
        gap: 12px;
        border-bottom: 1px solid #f1f5f9;
      }

      .recommendation-icon {
        font-size: 28px;
        flex-shrink: 0;
      }

      .recommendation-header h3 {
        font-size: 1.5rem;
        color: #0a2458;
        margin: 0;
        word-wrap: break-word;
      }

      .recommendation-body {
        padding: 32px;
      }

      @media (max-width: 768px) {
        .recommendation-body {
          padding: 24px;
        }
      }

      .recommendation-text {
        font-size: 1.125rem;
        line-height: 1.6;
        color: #374151;
        margin-bottom: 32px;
        word-wrap: break-word;
      }

      .recommendation-text strong {
        color: #0a2458;
      }

      .recommendation-cta {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
      }

      @media (max-width: 768px) {
        .recommendation-cta {
          flex-direction: column;
        }
      }

      .cta-primary {
        background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
        color: white;
        border: none;
        padding: 16px 32px;
        border-radius: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
        flex: 1;
        min-width: 200px;
      }

      .cta-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 25px rgba(10, 36, 88, 0.3);
      }

      .cta-secondary {
        background: transparent;
        color: #0a2458;
        border: 2px solid #0a2458;
        padding: 16px 32px;
        border-radius: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
        flex: 1;
        min-width: 200px;
      }

      .cta-secondary:hover {
        background: rgba(10, 36, 88, 0.05);
      }

      /* Summary Section - Fixed overlapping */
      .summary-section {
        background: #f8fafc;
        border-radius: 20px;
        padding: 40px;
        margin-top: 40px;
      }

      @media (max-width: 768px) {
        .summary-section {
          padding: 30px 20px;
        }
      }

      .summary-section h3 {
        text-align: center;
        color: #0a2458;
        font-size: 1.5rem;
        margin-bottom: 40px;
        word-wrap: break-word;
      }

      .summary-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 32px;
      }

      @media (max-width: 768px) {
        .summary-grid {
          grid-template-columns: 1fr;
          gap: 24px;
        }
      }

      .summary-item {
        background: white;
        border-radius: 16px;
        padding: 24px;
        display: flex;
        align-items: flex-start;
        gap: 16px;
        transition: transform 0.3s ease;
        min-height: 120px;
      }

      .summary-item:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
      }

      .summary-icon {
        font-size: 32px;
        flex-shrink: 0;
      }

      .summary-content {
        flex: 1;
      }

      .summary-content h4 {
        color: #0a2458;
        margin-bottom: 8px;
        font-size: 1.125rem;
        word-wrap: break-word;
      }

      .summary-content p {
        color: #0a2458;
        font-size: 0.875rem;
        margin: 0;
        line-height: 1.5;
        word-wrap: break-word;
      }

      /* Global text overflow prevention */
      * {
        box-sizing: border-box;
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        line-height: 1.3;
        margin-top: 0;
      }

      p {
        line-height: 1.6;
        margin-top: 0;
      }

      /* Responsive Design Improvements */
      @media (max-width: 768px) {
        .calculator-header {
          padding: 30px 20px;
        }

        .calculator-header h2 {
          font-size: 1.75rem;
        }

        .calculator-header p {
          font-size: 1rem;
        }

        .section-header h3 {
          font-size: 1.25rem;
        }

        .input-group {
          padding: 20px;
          min-height: auto;
        }

        .cost-value {
          font-size: 2rem;
        }

        .metric-value {
          font-size: 1.75rem;
        }
      }

      @media (max-width: 480px) {
        .hero-stats {
          gap: 20px;
        }

        .stat-item {
          min-width: 100px;
        }

        .stat-value {
          font-size: 2rem;
        }

        .input-group label {
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
        }

        .label-value {
          align-self: flex-start;
        }
      }
    `}</style>
  );
};
export default CustomSoftwareROICalculatorStyles;
